/* eslint- import/no-extraneous-dependencies */
import lod_ from "lodash";
import NotificationItem from "components/Advanced/Items/NotificationItem";
import MDBadge from "components/Basics/MDBadge";
import { useMaterialUIController } from "context";
import { navbarIconButton } from "components/Advanced/Navbars/DashboardNavbar/styles";
import MDBox from "components/Basics/MDBox";
import MDTypography from "components/Basics/MDTypography";
import notificationActions from "redux-react/actions/notificationActions";
import {
	markAllNotificationsAsRead,
	removeNotification,
	selectUnreadCount,
	selectNotificationsItems
} from "redux-react/reducers/notificationsReducer";
import "./style.css";
/**
 * #######################################################@
 *
 * Notifications component
 *
 * Used to display notifications
 *
 * #######################################################@
 */
import { Menu, IconButton, Icon, Tabs, Tab } from "@mui/material";
import { useEffect, useRef, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import MDButton from "components/Basics/MDButton";
import { List, CellMeasurer, CellMeasurerCache } from "react-virtualized";
import { AutoSizer } from "react-virtualized-auto-sizer";
import { t } from "i18next";
import { handleNotificationClick } from "helpers/notifications";

const Notifications = () => {
	const notificationsList = useSelector(selectNotificationsItems);
	const unreadCount = useSelector(selectUnreadCount);
	const dispatch = useDispatch();

	const [openMenu, setOpenMenu] = useState(false);
	const [activeTab, setActiveTab] = useState(0); // 0 = All, 1 = Unread
	const listRef = useRef(null);
	const cacheRef = useRef(
		new CellMeasurerCache({
			defaultHeight: 72,
			minHeight: 56,
			fixedWidth: true
		})
	);

	const handleOpenMenu = event => {
		setOpenMenu(event.currentTarget);
	};

	const handleCloseMenu = () => {
		setOpenMenu(false);
		setActiveTab(0); // Reset to "All" tab when closing
	};

	const handleTabChange = (event, newValue) => {
		setActiveTab(newValue);
	};

	/**
	 * Marquer toutes les notifications comme lues
	 */
	const handleMarkAllAsRead = () => {
		const readAt = new Date().toISOString();
		dispatch(markAllNotificationsAsRead({ readAt }));

		dispatch(
			notificationActions.markAllAsRead(
				() => {},
				() => {}
			)
		);
	};

	/**
	 * Supprimer une notification
	 * @param {string} notificationId - ID de la notification
	 */
	const handleDeleteNotification = notificationId => {
		if (lod_.isNil(notificationId) === true) {
			return;
		}

		dispatch(removeNotification({ id: notificationId }));

		dispatch(
			notificationActions.deleteNotification(
				notificationId,
				() => {},
				() => {}
			)
		);
	};

	const [controller] = useMaterialUIController();
	const { darkMode } = controller;

	// Filter notifications based on active tab
	const filteredNotifications = lod_
		.cloneDeep(notificationsList)
		.filter(notification => {
			if (activeTab === 0) {
				// Unread tab
				return notification.read !== true;
			}
			// All tab
			return true;
		})
		.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));

	// Styles for the navbar icons
	const iconsStyle = ({ palette: { dark, white, text }, functions: { rgba } }) => ({
		color: () => (darkMode ? white.main : dark.main)
	});

	useEffect(() => {
		cacheRef.current.clearAll();
		if (listRef.current) {
			listRef.current.recomputeRowHeights();
		}
	}, [filteredNotifications]);

	const rowRenderer = ({ index, key, parent, style }) => {
		const notification = filteredNotifications[index];

		if (lod_.isNil(notification) === true) {
			return null;
		}

		return (
			<CellMeasurer
				cache={cacheRef.current}
				columnIndex={0}
				rowIndex={index}
				parent={parent}
				key={key}
			>
				{({ registerChild }) => (
					<div ref={registerChild} style={{ ...style, width: "100%" }}>
						<NotificationItem
							key={notification.id || index}
							id={notification.id}
							icon={notification.icon}
							message={notification.message}
							createdAt={notification.createdAt}
							severity={notification.severity}
							read={notification.read}
							onClick={() => handleNotificationClick(notification, dispatch)}
							onDelete={() => handleDeleteNotification(notification.id)}
						/>
					</div>
				)}
			</CellMeasurer>
		);
	};

	const renderMenu = () => (
		<Menu
			anchorEl={openMenu}
			anchorReference={null}
			anchorOrigin={{
				vertical: "bottom",
				horizontal: "left"
			}}
			open={Boolean(openMenu)}
			onClose={handleCloseMenu}
			PaperProps={{
				sx: {
					width: "40vw",
					minWidth: "320px",
					maxWidth: "500px",
					height: "70vh",
					maxHeight: "70vh",
					p: 0,
					m: 0,
					display: "flex",
					flexDirection: "column",
					overflow: "hidden"
				}
			}}
			MenuListProps={{
				disablePadding: true,
				sx: {
					display: "flex",
					flexDirection: "column",
					height: "100%",
					overflow: "hidden"
				}
			}}
		>
			{/* Fixed Header */}
			<MDBox sx={{ borderBottom: 1, borderColor: "divider", flexShrink: 0 }}>
				<MDBox
					sx={{ px: 2, pt: 2, pb: 1 }}
					display="flex"
					alignItems="center"
					justifyContent="space-between"
				>
					<MDTypography variant="h6" fontWeight="medium" color="textPrimary">
						{t("NOTIFICATIONS.TITLE")}
					</MDTypography>

					<MDButton
						disabled={unreadCount <= 0}
						variant="text"
						color="info"
						size="small"
						onClick={handleMarkAllAsRead}
					>
						{t("NOTIFICATIONS.markAllAsRead")}
					</MDButton>
				</MDBox>
				{/* Fixed Tabs */}
				<Tabs
					value={activeTab}
					onChange={handleTabChange}
					sx={{
						px: 2,
						py: 1,
						mx: 1,
						mb: 1
					}}
				>
					<Tab
						label={
							<MDBox display="flex" alignItems="center" gap={1}>
								<MDTypography variant="button" fontWeight="medium">
									{t("NOTIFICATIONS.unread")}
								</MDTypography>
								{unreadCount > 0 && (
									<MDBadge
										badgeContent={unreadCount}
										color="error"
										size="xs"
										circular
										sx={{ ml: 0.5 }}
									/>
								)}
							</MDBox>
						}
						sx={{ textTransform: "none", minHeight: 48 }}
					/>
					<Tab
						label={
							<MDBox display="flex" alignItems="center" gap={1}>
								<MDTypography variant="button" fontWeight="medium">
									{t("NOTIFICATIONS.all")}
								</MDTypography>
								{notificationsList.length > 0 && (
									<MDBadge
										badgeContent={notificationsList.length}
										color="secondary"
										size="xs"
										circular
										sx={{ ml: 0.5 }}
									/>
								)}
							</MDBox>
						}
						sx={{ textTransform: "none", minHeight: 48 }}
					/>
				</Tabs>
			</MDBox>

			{/* Scrollable Notifications List */}
			<MDBox
				sx={{
					overflow: "hidden",
					flex: "1 1 auto",
					minHeight: 0
				}}
			>
				{filteredNotifications.length === 0 && (
					<MDBox display="flex" alignItems="center" justifyContent="center" py={4}>
						<MDTypography variant="caption" style={{ opacity: 0.7 }} fontWeight="bold">
							{activeTab === 0
								? t("NOTIFICATIONS.noUnreadNotifications")
								: t("NOTIFICATIONS.noNotifications")}
						</MDTypography>
					</MDBox>
				)}

				{filteredNotifications.length > 0 && (
					<AutoSizer>
						{({ height, width }) => (
							<List
								ref={listRef}
								height={height}
								width={width}
								rowCount={filteredNotifications.length}
								deferredMeasurementCache={cacheRef.current}
								rowHeight={cacheRef.current.rowHeight}
								rowRenderer={rowRenderer}
								overscanRowCount={6}
							/>
						)}
					</AutoSizer>
				)}
			</MDBox>
		</Menu>
	);

	return (
		<>
			<IconButton
				size="small"
				disableRipple
				color="inherit"
				sx={navbarIconButton}
				aria-controls="notification-menu"
				aria-haspopup="true"
				variant="contained"
				onClick={handleOpenMenu}
			>
				<MDBadge badgeContent={unreadCount} color="error" size="xs" circular>
					<Icon sx={iconsStyle}>notifications</Icon>
				</MDBadge>
			</IconButton>
			{renderMenu()}
		</>
	);
};

export default Notifications;
